import {
    FinancialDocument,
    FinancialDocumentExtended,
    ImporterErrors,
    SpreadsheetImporterStatus,
    TransactionsImportStatus,
    UNRECOVERABLE_IMPORTER_ERRORS,
} from "@common/types/financialDocument";
import { currencyFormatter } from "@common/helpers/currency";
import { IntegrationAccount } from "@common/types/integrationAccount";
import {
    getFinancialAccountType,
    hasBlockingImporterErrors,
    IMPORTER_BILLS_QUEUE,
    ImporterSpreadsheetColumns,
    isImporterReconciliationQueue,
    isImporterTransactionsQueue,
    NON_BLOCKING_IMPORTER_ERRORS,
} from "@common/helpers/financialDocument";
import { isLiabilityAccount } from "@common/helpers/financialAccount";
import {
    parseSpreadsheetDateValue,
    SpreadsheetValue,
} from "@common/helpers/spreadsheet";
import { capitalize, emptyToNull, pluralize } from "@common/helpers/string";
import { FinancialAccount } from "@common/types/financialAccount";
import { joinWithAnd } from "@helpers/joinWithAnd";
import { getDocumentFieldLockReason } from "../documents/lib";
import { ImporterDocumentRow } from "./types";

export enum ImporterDisplayErrorKind {
    IMPORTER_EXTRACTION = "importerExtraction",
    MISSING_ACCOUNT_DATA = "missingAccountData",
    WRONG_FINANCIAL_ACCOUNT = "wrongFinancialAccount",
    NOT_LINKED_TO_ACCOUNT = "notLinkedToAccount",
}

export type ImporterDisplayError =
    | {
          kind: ImporterDisplayErrorKind.IMPORTER_EXTRACTION;
          error: ImporterErrors;
      }
    | {
          kind: ImporterDisplayErrorKind.MISSING_ACCOUNT_DATA;
          financialDocument: FinancialDocumentExtended;
      }
    | {
          kind: ImporterDisplayErrorKind.WRONG_FINANCIAL_ACCOUNT;
      }
    | {
          kind: ImporterDisplayErrorKind.NOT_LINKED_TO_ACCOUNT;
      };

export const IMPORTER_ERROR_PRIORITY: Record<ImporterErrors, number> = {
    [ImporterErrors.PERIOD_IS_LOCKED]: 0,
    [ImporterErrors.PASSWORD_PROTECTED_PDF]: 1,
    [ImporterErrors.ACCRUAL_UNREADABLE_FILE]: 2,
    [ImporterErrors.EXTRACTION_FAILED]: 3,
    [ImporterErrors.ACCRUAL_NOT_ACCRUAL_DOCUMENT]: 4,
    [ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE]: 4,
    [ImporterErrors.ACCRUAL_WRONG_WORKSPACE]: 5,
    [ImporterErrors.INVALID_TRANSACTIONS]: 6,
    [ImporterErrors.BALANCES_OR_DATES_MISSING]: 7,
    [ImporterErrors.OPENING_DATE_AFTER_CLOSING_DATE]: 8,
    [ImporterErrors.NOT_RECONCILED]: 9,
    [ImporterErrors.DUPLICATE_FILE_UPLOADED]: 10,
    [ImporterErrors.SAME_PERIOD_ALREADY_UPLOADED]: 11,
    [ImporterErrors.STATEMENT_PERIOD_ALREADY_RECONCILED]: 12,
    [ImporterErrors.FOREIGN_CURRENCY]: 13,
    [ImporterErrors.ASSIGNED_FINANCIAL_ACCOUNT_MISMATCH]: 14,
    [ImporterErrors.NOT_CONFIDENT]: 15,
    [ImporterErrors.RECONCILED_WITH_INDETERMINABLE_SIGNS]: 16,
    [ImporterErrors.TRANSACTIONS_OUTSIDE_PERIOD]: 17,
    [ImporterErrors.TRANSACTIONS_BEFORE_BOOKKEEPING_START]: 18,
    [ImporterErrors.TRANSACTIONS_AFTER_BOOKKEEPING_END]: 19,
    [ImporterErrors.TRANSACTIONS_AFTER_ACCOUNT_BOOKKEEPING_END]: 20,
    [ImporterErrors.ACCRUAL_MISSING_ENTITY]: 21,
    [ImporterErrors.ACCRUAL_MISSING_COUNTERPARTY]: 22,
    [ImporterErrors.ACCRUAL_UNCONFIRMED_COUNTERPARTY]: 23,
    [ImporterErrors.ACCRUAL_COUNTERPARTY_DETAILS_CHANGED]: 24,
    [ImporterErrors.ACCRUAL_NO_LINE_ITEMS]: 25,
    [ImporterErrors.ACCRUAL_LINE_ITEMS_DONT_SUM_TO_TOTAL]: 26,
    [ImporterErrors.ACCRUAL_POSSIBLE_DUPLICATE]: 27,
    [ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS]: 28,
    [ImporterErrors.ACCRUAL_WRONG_ENTITY]: 29,
    [ImporterErrors.ACCRUAL_PARTIALLY_PAID]: 30,
    [ImporterErrors.ACCRUAL_MISSING_DOCUMENT_NUMBER]: 31,
    [ImporterErrors.ACCRUAL_MISSING_DUE_DATE]: 32,
    [ImporterErrors.ACCRUAL_DUE_DATE_BEFORE_DOCUMENT_DATE]: 32,
    [ImporterErrors.ACCRUAL_MISSING_ACCOUNT]: 33,
    [ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT]: 34,
    [ImporterErrors.ACCRUAL_UNCONFIRMED_ITEM]: 35,
};

const IMPORT_COMPLETED_STATUSES = [
    SpreadsheetImporterStatus.IMPORT_SUCCESS,
    SpreadsheetImporterStatus.IMPORT_FAILED,
    SpreadsheetImporterStatus.NEEDS_VERIFICATION,
];

const IMPORTER_ERROR_MESSAGES: Record<ImporterErrors, string> = {
    [ImporterErrors.NOT_RECONCILED]:
        "Transactions do not reconcile to change in balance",
    [ImporterErrors.RECONCILED_WITH_INDETERMINABLE_SIGNS]:
        "Double-check transaction signs", // <- Placeholder dynamically overriden
    [ImporterErrors.NOT_CONFIDENT]: "No discrepancy but want 2nd opinion",
    [ImporterErrors.FOREIGN_CURRENCY]: "Contains foreign currency",
    [ImporterErrors.SAME_PERIOD_ALREADY_UPLOADED]:
        "Same period already uploaded",
    [ImporterErrors.DUPLICATE_FILE_UPLOADED]: "File already uploaded",
    [ImporterErrors.STATEMENT_PERIOD_ALREADY_RECONCILED]:
        "Period already reconciled",
    [ImporterErrors.INVALID_TRANSACTIONS]: "Wrong or incomplete transactions",
    [ImporterErrors.ASSIGNED_FINANCIAL_ACCOUNT_MISMATCH]:
        "Appears to be from a different account",
    [ImporterErrors.BALANCES_OR_DATES_MISSING]:
        "Opening/closing date or balance missing", // <- Placeholder dynamically overriden
    [ImporterErrors.OPENING_DATE_AFTER_CLOSING_DATE]:
        "Opening date is after closing date",
    [ImporterErrors.EXTRACTION_FAILED]:
        "Could not automatically extract transactions from this document",
    [ImporterErrors.PASSWORD_PROTECTED_PDF]:
        "This PDF is password-protected. Please remove the password and re-upload.",
    [ImporterErrors.PERIOD_IS_LOCKED]:
        "Unlock this period to continue with reconciliation", // <- Placeholder dynamically overriden
    [ImporterErrors.TRANSACTIONS_OUTSIDE_PERIOD]:
        "Some extracted transactions have dates outside the statement period",
    [ImporterErrors.TRANSACTIONS_BEFORE_BOOKKEEPING_START]:
        "Some transactions are dated before your bookkeeping start date and won't appear on the transactions page",
    [ImporterErrors.TRANSACTIONS_AFTER_BOOKKEEPING_END]:
        "Some transactions are dated after your bookkeeping end date and won't appear on the transactions page",
    [ImporterErrors.TRANSACTIONS_AFTER_ACCOUNT_BOOKKEEPING_END]:
        "Some transactions are dated after this account's bookkeeping end date and won't appear on the transactions page",
    [ImporterErrors.ACCRUAL_MISSING_ENTITY]: "Entity is required",
    [ImporterErrors.ACCRUAL_MISSING_COUNTERPARTY]: "Counterparty is required",
    [ImporterErrors.ACCRUAL_UNCONFIRMED_COUNTERPARTY]:
        "Confirm the suggested counterparty",
    [ImporterErrors.ACCRUAL_COUNTERPARTY_DETAILS_CHANGED]:
        "New email, phone, contact, or address differs from the saved counterparty",
    [ImporterErrors.ACCRUAL_WRONG_WORKSPACE]:
        "This document appears to belong to another workspace",
    [ImporterErrors.ACCRUAL_WRONG_ENTITY]:
        "This document appears to belong to another entity",
    [ImporterErrors.ACCRUAL_UNREADABLE_FILE]:
        "This file could not be read. Check it is not password protected or corrupted, then re-upload.",
    [ImporterErrors.ACCRUAL_NOT_ACCRUAL_DOCUMENT]: "Not an invoice or bill",
    [ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE]: "Looks like a bill",
    [ImporterErrors.ACCRUAL_NO_LINE_ITEMS]:
        "At least one line item with a non-zero amount is required",
    [ImporterErrors.ACCRUAL_LINE_ITEMS_DONT_SUM_TO_TOTAL]:
        "Line items do not sum to the document total",
    [ImporterErrors.ACCRUAL_POSSIBLE_DUPLICATE]:
        "Possible duplicate: same number and entity",
    [ImporterErrors.ACCRUAL_PARTIALLY_PAID]:
        "Balance due is lower than the total. Record the payment after import",
    [ImporterErrors.ACCRUAL_MISSING_DOCUMENT_NUMBER]: "Number is required",
    [ImporterErrors.ACCRUAL_MISSING_DUE_DATE]: "Due date is required",
    [ImporterErrors.ACCRUAL_DUE_DATE_BEFORE_DOCUMENT_DATE]:
        "Due date must be on or after the document date",
    [ImporterErrors.ACCRUAL_MISSING_ACCOUNT]:
        "Every line item needs an account",
    [ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT]:
        "Confirm the suggested account",
    [ImporterErrors.ACCRUAL_UNCONFIRMED_ITEM]: "Confirm the suggested item",
    [ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS]:
        "Omitted the bank-funded transactions to correctly reconcile Cash App activity",
};

export function isTransactionsProcessingFinished(document: FinancialDocument) {
    return IMPORT_COMPLETED_STATUSES.includes(
        document.spreadsheetImporterStatus,
    );
}

function hasMissingStatementBalances(
    financialDocument: FinancialDocument,
): boolean {
    return (
        financialDocument.openingBalance === null ||
        financialDocument.closingBalance === null
    );
}

export function shouldOfferFlipSignsInPreview(
    financialDocument: FinancialDocument,
    {
        onFlipSigns,
        activeImporterError,
    }: {
        onFlipSigns?: () => void;
        activeImporterError?: ImporterErrors;
    },
): boolean {
    if (!onFlipSigns) {
        return false;
    }
    if (hasMissingStatementBalances(financialDocument)) {
        return true;
    }
    return (
        activeImporterError ===
            ImporterErrors.RECONCILED_WITH_INDETERMINABLE_SIGNS ||
        activeImporterError === ImporterErrors.NOT_RECONCILED
    );
}

export function isImportRunning(document: FinancialDocument) {
    return (
        document.transactionsImportStatus ===
            TransactionsImportStatus.IN_PROGRESS ||
        document.transactionsImportStatus === TransactionsImportStatus.SCHEDULED
    );
}

export function documentBeingProcessed(document: FinancialDocument) {
    return (
        !document.accountInfoExtracted ||
        !isTransactionsProcessingFinished(document)
    );
}

type ReconciliationAccountStatus =
    | "ready"
    | "wrongAccount"
    | "notLinkedToAccount";

function getReconciliationAccountStatus(
    financialDocument: FinancialDocument,
    reconciliedFinancialAccount: FinancialAccount,
): ReconciliationAccountStatus {
    if (financialDocument.financialAccountId == null) {
        return "notLinkedToAccount";
    }

    if (
        financialDocument.financialAccountId !== reconciliedFinancialAccount.id
    ) {
        return "wrongAccount";
    }

    return "ready";
}

function isImporterAccountDataPresent(
    financialDocument: FinancialDocumentExtended,
) {
    return Boolean(
        financialDocument.financialAccountId ??
        (financialDocument.accountInfoExtraction?.accountType &&
            financialDocument.entityId &&
            financialDocument.accountInfoExtraction?.institutionName),
    );
}

export function isRowReadyForImport(
    row: ImporterDocumentRow,
    importerQueue: string,
    reconciliedFinancialAccount?: FinancialAccount,
): boolean {
    const financialDocument = row.financialDocument;

    if (
        !financialDocument ||
        (financialDocument.transactionsImportStatus ===
            TransactionsImportStatus.FINISHED &&
            isImporterTransactionsQueue(importerQueue))
    ) {
        return false;
    }

    const isAccountReady = reconciliedFinancialAccount
        ? getReconciliationAccountStatus(
              financialDocument,
              reconciliedFinancialAccount,
          ) === "ready"
        : isImporterAccountDataPresent(financialDocument);

    return Boolean(
        isAccountReady &&
        !hasBlockingImporterErrors(financialDocument) &&
        isTransactionsProcessingFinished(financialDocument),
    );
}

function getMissingFieldsMessage(financialDocument: FinancialDocument): string {
    const missing: string[] = [];
    if (financialDocument.openingDate === null) {
        missing.push("opening date");
    }
    if (financialDocument.openingBalance === null) {
        missing.push("opening balance");
    }
    if (financialDocument.closingDate === null) {
        missing.push("closing date");
    }
    if (financialDocument.closingBalance === null) {
        missing.push("closing balance");
    }

    if (missing.length === 0) {
        return "Opening/closing date or balance"; // Should never happen
    } else if (missing.length === 1) {
        return capitalize(`${missing[0]} is missing`);
    } else if (missing.length === 2) {
        return capitalize(`${missing[0]} and ${missing[1]} are missing`);
    } else if (missing.length === 4) {
        return "Opening and closing balances and dates are missing";
    } else {
        return capitalize(
            `${missing.slice(0, -1).join(", ")}, and ${missing[missing.length - 1]} are missing`,
        );
    }
}

function getReconciledWithIndeterminableSignsMessage(
    financialDocument: FinancialDocumentExtended,
): string {
    const accountType = getFinancialAccountType(financialDocument);
    const isCreditCard = accountType ? isLiabilityAccount(accountType) : false;

    // Default to bank account message if account type is unknown
    return isCreditCard
        ? "Confirm payments appear under 'Cash in' and expenses appear under 'Cash out'"
        : "Confirm incoming amounts appear under 'Cash in' and outgoing amounts under 'Cash out'";
}

function getPeriodLockedMessage(financialDocument: FinancialDocument): string {
    if (
        financialDocument.importerQueue &&
        isImporterReconciliationQueue(financialDocument.importerQueue)
    ) {
        return "Unlock this period to continue with reconciliation";
    }
    return "Unlock this period to continue with importing";
}

function getCashAppExcludedLinkedPaymentsMessage(
    financialDocument: FinancialDocumentExtended,
): string {
    const excludedCount =
        financialDocument.cashAppExcludedLinkedAccountPaymentCount;

    if (excludedCount == null || excludedCount <= 0) {
        return IMPORTER_ERROR_MESSAGES[
            ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS
        ];
    }

    return `Omitted the ${excludedCount} bank-funded ${pluralize("transaction", excludedCount)} to correctly reconcile Cash App activity`;
}

function getImporterErrorMessage(
    error: ImporterErrors,
    financialDocument: FinancialDocumentExtended,
): string {
    if (error === ImporterErrors.RECONCILED_WITH_INDETERMINABLE_SIGNS) {
        return getReconciledWithIndeterminableSignsMessage(financialDocument);
    }
    if (error === ImporterErrors.BALANCES_OR_DATES_MISSING) {
        return getMissingFieldsMessage(financialDocument);
    }
    if (error === ImporterErrors.PERIOD_IS_LOCKED) {
        return getPeriodLockedMessage(financialDocument);
    }
    if (error === ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS) {
        return getCashAppExcludedLinkedPaymentsMessage(financialDocument);
    }
    if (
        error === ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE &&
        financialDocument.importerQueue === IMPORTER_BILLS_QUEUE
    ) {
        return "Looks like an invoice";
    }
    return IMPORTER_ERROR_MESSAGES[error];
}

export interface InvalidTransactionsSummary {
    hasMissingDates: boolean;
    hasMissingAmounts: boolean;
    hasMissingDescriptions: boolean;
}

export function getInvalidTransactionsSummary(
    data: Array<Record<string, SpreadsheetValue>>,
): InvalidTransactionsSummary {
    const nonEmptyRows = data.filter((row) =>
        Object.values(row).some((value) => value !== null),
    );

    let hasMissingDates = false;
    let hasMissingAmounts = false;
    let hasMissingDescriptions = false;

    for (const row of nonEmptyRows) {
        const dateValue = row[ImporterSpreadsheetColumns.DATE];
        if (
            !dateValue ||
            typeof dateValue !== "string" ||
            !parseSpreadsheetDateValue(dateValue)
        ) {
            hasMissingDates = true;
        }

        const amountValue = row[ImporterSpreadsheetColumns.AMOUNT];
        if (typeof amountValue !== "number" || !Number.isFinite(amountValue)) {
            hasMissingAmounts = true;
        }

        const descriptionValue = row[ImporterSpreadsheetColumns.DESCRIPTION];
        if (!descriptionValue || typeof descriptionValue !== "string") {
            hasMissingDescriptions = true;
        }

        if (hasMissingDates && hasMissingAmounts && hasMissingDescriptions) {
            break;
        }
    }

    return { hasMissingDates, hasMissingAmounts, hasMissingDescriptions };
}

function getInvalidTransactionsMessage(
    summary: InvalidTransactionsSummary,
): string {
    const missing: string[] = [];
    if (summary.hasMissingDates) {
        missing.push("dates");
    }
    if (summary.hasMissingAmounts) {
        missing.push("amounts");
    }
    if (summary.hasMissingDescriptions) {
        missing.push("descriptions");
    }

    if (missing.length === 0) {
        return IMPORTER_ERROR_MESSAGES[ImporterErrors.INVALID_TRANSACTIONS];
    }
    return `Some transactions are missing ${joinWithAnd(missing)}`;
}

export function getInformationalImporterErrors(
    financialDocument: FinancialDocumentExtended,
): ImporterDisplayError[] {
    return (financialDocument.importerErrors ?? [])
        .filter((error) => NON_BLOCKING_IMPORTER_ERRORS.includes(error))
        .filter(
            (error) =>
                !financialDocument.dismissedImporterErrors?.includes(error),
        )
        .map((error) => ({
            kind: ImporterDisplayErrorKind.IMPORTER_EXTRACTION,
            error,
        }));
}

export function getDisplayedErrors(
    financialDocument: FinancialDocumentExtended,
    reconciliedFinancialAccount?: FinancialAccount,
): ImporterDisplayError[] {
    const errors: ImporterDisplayError[] = (
        financialDocument.importerErrors ?? []
    )
        .filter((e) => e !== ImporterErrors.NOT_CONFIDENT)
        .filter((e) => !NON_BLOCKING_IMPORTER_ERRORS.includes(e))
        .toSorted(
            (a, b) => IMPORTER_ERROR_PRIORITY[a] - IMPORTER_ERROR_PRIORITY[b],
        )
        .map((error) => ({
            kind: ImporterDisplayErrorKind.IMPORTER_EXTRACTION,
            error,
        }));

    const hasUnrecoverableError = financialDocument.importerErrors?.some((e) =>
        UNRECOVERABLE_IMPORTER_ERRORS.has(e),
    );

    if (!hasUnrecoverableError) {
        if (reconciliedFinancialAccount) {
            const status = getReconciliationAccountStatus(
                financialDocument,
                reconciliedFinancialAccount,
            );
            if (status === "wrongAccount") {
                errors.push({
                    kind: ImporterDisplayErrorKind.WRONG_FINANCIAL_ACCOUNT,
                });
            } else if (status === "notLinkedToAccount") {
                errors.push({
                    kind: ImporterDisplayErrorKind.NOT_LINKED_TO_ACCOUNT,
                });
            }
        } else if (!isImporterAccountDataPresent(financialDocument)) {
            errors.push({
                kind: ImporterDisplayErrorKind.MISSING_ACCOUNT_DATA,
                financialDocument,
            });
        }
    }

    return errors;
}

interface GetDisplayErrorMessageOptions {
    discrepancyAmount?: number;
    invalidTransactionsSummary?: InvalidTransactionsSummary;
}

export function getDisplayErrorMessage(
    displayError: ImporterDisplayError,
    financialDocument: FinancialDocumentExtended,
    options?: GetDisplayErrorMessageOptions,
): string {
    if (
        displayError.kind === ImporterDisplayErrorKind.WRONG_FINANCIAL_ACCOUNT
    ) {
        return "Appears to be from a different account";
    }

    if (displayError.kind === ImporterDisplayErrorKind.NOT_LINKED_TO_ACCOUNT) {
        return "Select an existing account in Account #";
    }

    if (displayError.kind === ImporterDisplayErrorKind.MISSING_ACCOUNT_DATA) {
        return getMissingAccountDataMessage(displayError.financialDocument);
    }

    if (
        displayError.error === ImporterErrors.INVALID_TRANSACTIONS &&
        options?.invalidTransactionsSummary
    ) {
        return getInvalidTransactionsMessage(
            options.invalidTransactionsSummary,
        );
    }

    const message = getImporterErrorMessage(
        displayError.error,
        financialDocument,
    );

    if (
        displayError.error === ImporterErrors.NOT_RECONCILED &&
        options?.discrepancyAmount !== undefined
    ) {
        return `${message}. Discrepancy: ${currencyFormatter.format(options.discrepancyAmount)}`;
    }

    return message;
}

function getMissingAccountDataMessage(
    financialDocument: FinancialDocumentExtended,
): string {
    if (financialDocument.financialAccountId) {
        return "Fill account info and select entity"; // Should never happen
    }

    const missing: string[] = [];
    if (!financialDocument.accountInfoExtraction?.institutionName) {
        missing.push("institution name");
    }
    if (!financialDocument.accountInfoExtraction?.accountType) {
        missing.push("account type");
    }
    if (!financialDocument.entityId) {
        missing.push("entity");
    }

    if (missing.length === 0) {
        return "Fill account info and select entity"; // Should never happen
    }
    return `Fill in ${joinWithAnd(missing)}`;
}

export { emptyToNull };

export function getLinkedRowTooltipText(
    integrationAccount: IntegrationAccount | undefined,
    financialDocument: FinancialDocument,
): string | null {
    const reason = getDocumentFieldLockReason(
        financialDocument,
        integrationAccount,
    );
    switch (reason) {
        case "reconciled":
            return "Cannot edit on already reconciled document.";
        case "imported":
            return "Cannot edit on already imported document";
        case "linked-account":
            return "Unlink the financial account to edit this field.";
        default:
            return null;
    }
}
